import React from 'react'
import { cn } from '@/lib/utils'

export interface SwitchProps {
  /** Switch label */
  label?: string
  /** Checked state */
  checked: boolean
  /** Change handler */
  onChange: (checked: boolean) => void
  /** Help text */
  helpText?: string
  /** Disabled state */
  disabled?: boolean
  /** Input name */
  name?: string
  /** Input ID */
  id?: string
  /** Additional class name */
  className?: string
  /** Data ID for testing */
  'data-id'?: string
}

export const Switch: React.FC<SwitchProps> = ({
  label,
  checked,
  onChange,
  helpText,
  disabled = false,
  name,
  id,
  className,
  'data-id': dataId,
}) => {
  const handleChange = () => {
    if (!disabled) {
      onChange(!checked)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault()
      handleChange()
    }
  }

  return (
    <div
      className={cn('flex items-center gap-3', className)}
      data-id={dataId}
    >
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-disabled={disabled}
        id={id}
        onClick={handleChange}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={cn(
          'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          disabled && 'cursor-not-allowed opacity-60',
          checked ? 'bg-primary' : 'bg-muted-foreground'
        )}
      >
        <span
          className={cn(
            'pointer-events-none inline-block h-5 w-5 rounded-full bg-background shadow-lg transform ring-0 transition-transform duration-200 ease-in-out mt-0.5',
            checked ? 'translate-x-5 ml-0.5' : 'translate-x-0.5'
          )}
        />
      </button>
      {(label || helpText) && (
        <div className="flex flex-col">
          {label && (
            <label
              htmlFor={id}
              className={cn(
                'text-[14px] leading-5 cursor-pointer select-none',
                disabled ? 'text-muted-foreground' : 'text-foreground'
              )}
              onClick={handleChange}
            >
              {label}
            </label>
          )}
          {helpText && (
            <span className="text-[12px] text-muted-foreground">{helpText}</span>
          )}
        </div>
      )}
      {name && (
        <input
          type="hidden"
          name={name}
          value={checked ? 'true' : 'false'}
        />
      )}
    </div>
  )
}
